<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />
    <div class="overflow-y-auto max-h-full pb-[100px]">
      <div class="flex w-full items-center justify-between px-4 py-4">
        <a @click="goBack" class="text-prime-golden text-xl flex gap-2 items-center">
          <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back Button" />
          <p
            v-if="
              !projectId &&
              !route.query.from_call &&
              !route.query.from_text &&
              !route.query.from_email &&
              !route.query.from_calendar
            "
          >
            All Contacts
          </p>
        </a>
        <div class="flex gap-6">
          <button
            v-if="isPartiallyEmpty"
            @click="mergeContact"
            class="text-prime-golden text-sm text-md"
          >
            <img
              src="@/assets/images/user.svg"
              class="mr-2 fa"
              alt="Edit"
              width="16"
              height="16"
            />
            <span class="underline underline-offset-4">merge</span>
          </button>
          <button
            v-if="contact.role !== 'Project Manager'"
            @click="editContact"
            class="text-prime-golden text-sm text-md"
          >
            <img
              src="@/assets/images/edit-pen.svg"
              class="mr-1 fa"
              alt="Edit"
              width="12"
              height="12"
            />
            <span class="underline underline-offset-4">edit</span>
          </button>
        </div>
      </div>

      <!-- Contact Details -->
      <div
        :style="{ opacity: isFetching ? '40%' : '100%' }"
        class="flex-1 overflow-y-auto p-4"
      >
        <div v-if="contact" class="flex flex-col items-center">
          <div class="flex flex-col items-center">
            <div
              class="w-20 h-20 rounded-full flex items-center mb-4 justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
            >
              <template v-if="contact.profile_image_url">
                <img
                  :src="contact.profile_image_url"
                  alt="Profile Image"
                  class="w-full h-full object-cover rounded-full border"
                />
              </template>
              <template v-else>
                <!-- Placeholder icon -->
                <i
                  class="fa-solid fa-user text-6xl text-pitch-black -mb-[1px] mt-auto"
                ></i>
              </template>
            </div>

            <h2 class="text-3xl mb-2" :class="{ 'mb-8': isSelf }">
              {{ contact.first_name }} {{ contact.last_name }}
              {{ contact.role == "Project Manager" ? " (You)" : "" }}
            </h2>
            <div class="flex gap-6 text-sm mb-10 mt-4" v-if="!isSelf">
              <!-- If the contact is a Project Agent, show only Text and Email buttons -->
              <template v-if="contact.role === 'Project Agent'">
                <button
                  v-if="contact.phone"
                  class="flex flex-col w-20 py-3 pb-[10px] border border-light-grey-translucent-700 px-6 gap-1 bg-light-grey-translucent-500 text-prime-golden items-center rounded-xl"
                  @click="textContact"
                >
                  <img
                    class="place-self-center h-[14px]"
                    src="../../assets/images/msg-icon.svg"
                    alt="Text Icon"
                  />
                  <span>text</span>
                </button>
                <button
                  v-if="contact.email"
                  class="flex flex-col py-3 w-20 pb-[10px] border border-light-grey-translucent-700 px-6 gap-1 bg-light-grey-translucent-500 text-prime-golden items-center rounded-xl"
                  @click="emailContact"
                >
                  <i class="fas fa-envelope text-prime-golden-800"></i>
                  <span>email</span>
                </button>
              </template>
              <!-- Otherwise, for any other role, show all three buttons -->
              <template v-else>
                <button
                  v-if="contact.phone"
                  class="flex flex-col w-20 py-3 pb-[10px] border border-light-grey-translucent-700 px-6 gap-1 bg-light-grey-translucent-500 text-prime-golden items-center rounded-xl"
                  @click="callContact"
                >
                  <i class="fas fa-phone text-[13px]"></i>
                  <span>call</span>
                </button>
                <button
                  v-if="contact.phone"
                  class="flex flex-col w-20 py-3 pb-[10px] border border-light-grey-translucent-700 px-6 gap-1 bg-light-grey-translucent-500 text-prime-golden items-center rounded-xl"
                  @click="textContact"
                >
                  <img
                    class="place-self-center h-[14px]"
                    src="../../assets/images/msg-icon.svg"
                    alt="Text Icon"
                  />
                  <span>text</span>
                </button>
                <button
                  v-if="contact.email"
                  class="flex flex-col py-3 w-20 pb-[10px] border border-light-grey-translucent-700 px-6 gap-1 bg-light-grey-translucent-500 text-prime-golden items-center rounded-xl"
                  @click="emailContact"
                >
                  <i class="fas fa-envelope text-prime-golden-800"></i>
                  <span>email</span>
                </button>
              </template>
            </div>
          </div>

          <!-- Contact Info -->
          <div class="text-left md:text-center w-full px-2">
            <div class="mb-6">
              <h3 class="font-semibold">Email Address</h3>
              <p class="text-sm py-2">{{ contact.email }}</p>
            </div>
            <div class="mb-6">
              <h3 class="font-semibold">
                {{
                  contact.role === "Project Manager"
                    ? "Project Inbox Phone Number"
                    : "Phone Number"
                }}
              </h3>
              <p class="text-sm py-2">{{ contact.phone }}</p>
            </div>
            <div class="mb-6">
              <h3 class="font-semibold">Company</h3>
              <p class="text-sm py-2">{{ contact.company }}</p>
            </div>
            <div class="mb-6">
              <h3 class="font-semibold">Role</h3>
              <p class="text-sm py-2">{{ contact.role }}</p>
            </div>
            <div class="mb-6">
              <div
                class="flex md:justify-center items-center gap-2 w-full justify-between"
              >
                <h3 class="font-semibold">Related Projects</h3>
                <button @click="addProject" class="text-prime-golden text-lg">
                  <i class="fas fa-plus"></i>
                </button>
              </div>
              <ul class="mt-2 space-y-2">
                <li v-for="project in contact.projects" :key="project.id">
                  <a
                    @click="openProject(project.id)"
                    target="_blank"
                    class="text-prime-golden underline flex md:justify-center items-center"
                  >
                    <span>{{ project.address_1 }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-prime-golden">Loading contact details...</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import { useContactsStore } from "@/stores/contact";
import { useLogStore } from "@/stores/log";
import { useProjectStore } from "@/stores/project";

const route = useRoute();
const router = useRouter();
const contactId = ref(null);
const isFetching = ref(false);
const contact = ref({});
const contactStore = useContactsStore();
const logStore = useLogStore();
const projectId = route.query.projectId;

const projectStore = useProjectStore();

const projectManagerPhone = computed(() => {
  // First try to get it from the current project
  if (projectStore.currentProject?.project_manager_twilio_number?.phone_number) {
    return projectStore.currentProject.project_manager_twilio_number.phone_number;
  }
  // Fall back to looking it up in contactsStore (if available)
  if (contactStore.contacts && contactStore.contacts.length) {
    return contactStore.contacts.find((c) => c.role === "Project Manager")?.phone || "";
  }
  return "";
});
// Fetch contact details by ID
const fetchContact = async (id) => {
  try {
    isFetching.value = true;
    await contactStore.fetchContactById(id);
    contact.value = contactStore.currentContact;
    console.log(contact, contact.value);
  } catch (error) {
    console.error("Failed to fetch contact:", error);
    alert("Error fetching contact details."); // User feedback
  } finally {
    isFetching.value = false;
  }
};

onMounted(() => {
  contactId.value = router.currentRoute.value.params.id; // Get the ID from route params
  fetchContact(contactId.value); // Fetch the contact data
});

const isSelf = computed(() => {
  return contact.value.role === "Project Manager";
});

const isPartiallyEmpty = computed(() => {
  return contact.value.phone == null || contact.value.email == null;
});

const goBack = () => {
  // If we have any "from" flag, use history back
  if (
    route.query.from_contact ||
    route.query.from_call ||
    route.query.from_email ||
    route.query.from_text ||
    route.query.from_calendar
  ) {
    router.go(-1);
  } else if (route.query.tab) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId || route.params.projectId },
      query: { tab: route.query.tab, readLogs: route.query.readLogs },
    });
  } else if (route.query.projectId) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId || route.params.projectId },
      query: { tab: route.query.tab || "contacts" },
    });
  } else {
    router.push({ name: "ContactBookPage" });
  }
};

const mergeContact = () => {
  router.push({
    name: "MergeContactPage",
    params: { id: contactId.value },
    query: { projectId: projectId },
  });
};

const editContact = () => {
  router.push({
    name: "ContactEditPage",
    params: { id: contactId.value },
    query: { projectId: projectId },
  });
};

const callContact = () => {
  router.push({
    name: "NewCall",
    params: { projectId: projectId, contactId: contactId.value },
  });
};

const textContact = async () => {
  if (projectId) {
    const existingThreadId = await logStore.findTextThread(Number(projectId), [
      contact.value.phone,
      projectManagerPhone.value,
    ]);
    if (existingThreadId) {
      router.push({
        name: "TextPage",
        params: {
          threadId: existingThreadId,
          projectId: projectId,
          contactId: contactId.value,
        },
        query: { from_contact: true },
      });
    } else {
      router.push({
        name: "NewTextMessage",
        params: { contactId: contactId.value, projectId: projectId },
        query: { from_contact: true },
      });
    }
  } else {
    router.push({
      name: "NewTextMessage",
      params: { contactId: contactId.value },
      query: { from_contact: true },
    });
  }
};

const emailContact = () => {
  if (projectId) {
    router.push({
      name: "NewEmail",
      params: { contactId: contactId.value, projectId: projectId },
      query: { contactId: contactId.value, projectId: projectId, from_contact: true },
    });
  } else {
    router.push({
      name: "NewEmail",
      params: { contactId: contactId.value },
      query: { contactId: contactId.value, from_contact: true },
    });
  }
};

const openProject = (projectId) => {
  router.push({ name: "ProjectPage", params: { id: projectId } });
};

const addProject = () => {
  router.push({
    name: "AssignProjectToContact",
    params: { id: contactId.value, projectId: route.query.projectId },
  });
};
</script>

<style scoped></style>
